import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, ExternalLink, GitBranch, GitFork, Star, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import Masonry from "react-masonry-css";
import { trpc } from "@/app/api/trpcClient";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/components/ui/avatar";
import { Badge } from "@/app/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/components/ui/card";

export const Route = createFileRoute("/github-news")({
  component: GithubNewsPage,
});

function GithubNewsPage() {
  const {
    data: newsData,
    isLoading,
    error,
  } = trpc.github.news.useQuery({
    limit: 30,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading GitHub releases...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Error loading releases: {error.message}</div>
      </div>
    );
  }

  if (!newsData || newsData.recentReleases.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No GitHub releases found. Run ETL to sync repositories.</div>
      </div>
    );
  }

  // Group releases by repository
  const releasesByRepo = newsData.releasesByRepo;

  return (
    <div className="h-full overflow-x-hidden">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-3xl font-bold">🚀 GitHub Releases</h1>
        <p className="text-gray-600 mt-2">
          Recent releases from {newsData.totalRepos} tracked repositories • {newsData.totalReleases}{" "}
          total releases
        </p>
      </div>

      {/* Repository Stats Summary */}
      <div className="px-6 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {newsData.repos.slice(0, 4).map((repo) => (
            <Card key={repo.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm truncate flex-1">
                    <a
                      href={repo.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-600 flex items-center gap-1"
                    >
                      {repo.name}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </h3>
                  {repo.language && (
                    <Badge variant="outline" className="text-xs">
                      {repo.language}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    {repo.stargazers_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <GitFork className="h-3 w-3" />
                    {repo.forks_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {repo.open_issues_count}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Releases */}
      <Masonry
        breakpointCols={{
          default: 3,
          1400: 2,
          900: 1,
        }}
        className="flex w-auto px-6 pb-6"
        columnClassName="bg-clip-padding pl-4 first:pl-0"
      >
        {Object.entries(releasesByRepo).map(([repoName, releases]) => {
          // Only show repos with releases
          if (!releases || releases.length === 0) return null;

          return (
            <Card key={repoName} className="overflow-hidden mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-gray-500" />
                      <a
                        href={releases[0]?.html_url.replace(/\/releases\/.*$/, "")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-blue-600 truncate"
                      >
                        {repoName}
                      </a>
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {releases.length} release{releases.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {releases.slice(0, 3).map((release) => (
                    <div key={release.id} className="border-l-2 border-gray-200 pl-4 py-2">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <a
                              href={release.html_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Tag className="h-3 w-3" />
                              {release.name || release.tag_name}
                            </a>
                            {release.draft && (
                              <Badge variant="secondary" className="text-xs">
                                Draft
                              </Badge>
                            )}
                            {release.prerelease && (
                              <Badge variant="outline" className="text-xs">
                                Pre-release
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                {release.author_avatar_url ? (
                                  <AvatarImage
                                    src={release.author_avatar_url}
                                    alt={release.author_login}
                                  />
                                ) : null}
                                <AvatarFallback className="text-xs">
                                  {release.author_login.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <a
                                href={release.author_html_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-600 hover:text-blue-600"
                              >
                                {release.author_login}
                              </a>
                            </div>
                            <span className="text-xs text-gray-500">
                              {release.published_at
                                ? formatDistanceToNow(new Date(release.published_at), {
                                    addSuffix: true,
                                  })
                                : release.created_at
                                  ? formatDistanceToNow(new Date(release.created_at), {
                                      addSuffix: true,
                                    })
                                  : "Unknown time"}
                            </span>
                          </div>
                        </div>
                      </div>
                      {release.body && (
                        <div className="text-sm text-gray-700 prose prose-sm max-w-none mt-2">
                          <ReactMarkdown
                            components={{
                              h1: ({ children }) => (
                                <h1 className="text-lg font-bold text-gray-800 mt-2 mb-1">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className="text-base font-semibold text-gray-800 mt-2 mb-1">
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className="text-sm font-semibold text-gray-800 mt-2 mb-1">
                                  {children}
                                </h3>
                              ),
                              ul: ({ children }) => (
                                <ul className="list-disc pl-4 space-y-0.5 text-xs">{children}</ul>
                              ),
                              li: ({ children }) => <li className="text-gray-700">{children}</li>,
                              p: ({ children }) => <p className="mb-1 text-xs">{children}</p>,
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  className="text-blue-600 hover:underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              ),
                              code: ({ children }) => (
                                <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                                  {children}
                                </code>
                              ),
                              pre: ({ children }) => (
                                <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                                  {children}
                                </pre>
                              ),
                            }}
                          >
                            {release.body.length > 500
                              ? `${release.body.slice(0, 500)}...`
                              : release.body}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  ))}
                  {releases.length > 3 && (
                    <div className="text-sm text-gray-500 italic pl-4">
                      ... and {releases.length - 3} more releases
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </Masonry>
    </div>
  );
}
